<!--
  ClientApprovalsPanel.vue -- embedded F1.e approval queue scoped to one
  Client.

  Consumes ``GET /api/approval_records?client_id=<n>`` (the Phase 2.3
  client_id filter extension). Renders the top 5 most-urgent rows so
  the Distributor can triage from the Client detail page without
  jumping into the full Approval Workspace.

  Urgency order (matches the workspace's "Needs attention" precedence
  per the implementation-plan §P2-Q2 rule):
    rejected > changes_requested > stale > awaiting_approval > else
-->
<template>
    <div class="cap" data-testid="client-approvals-panel">
        <div v-if="loading" class="cap__loading">Loading approvals...</div>
        <div v-else-if="error" class="cap__error">{{ error }}</div>
        <p v-else-if="visibleRows.length === 0" class="cap__empty">
            No pending approvals.
        </p>
        <ul v-else class="cap__list">
            <li
                v-for="r in visibleRows"
                :key="r.id"
                class="cap__item"
                :data-testid="`client-approvals-row-${r.id}`"
            >
                <component
                    :is="linkRows ? 'router-link' : 'div'"
                    class="cap__link"
                    v-bind="linkRows ? { to: `/distributor/decorations/approvals/${r.id}` } : {}"
                >
                    <span class="cap__status">{{ statusLabel(r.status) }}</span>
                    <span class="cap__updated">{{ relTime(r.updated_at) }}</span>
                    <span
                        v-if="r.stale_state && r.stale_state !== 'active'"
                        class="cap__stale"
                    >
                        {{ staleLabel(r.stale_state) }}
                    </span>
                </component>
            </li>
        </ul>
    </div>
</template>

<script>
import api from '@/services/api'

const STATUS_RANK = {
    rejected: 0,
    changes_requested: 1,
    awaiting_approval: 2,
    digitizing: 3,
    uploaded: 4,
    approved: 5,
}

const STATUS_LABEL = {
    uploaded: 'Uploaded',
    digitizing: 'Digitizing',
    awaiting_approval: 'Awaiting approval',
    approved: 'Approved',
    changes_requested: 'Changes requested',
    rejected: 'Rejected',
}

const STALE_LABEL = {
    reminded: 'Reminder sent',
    escalated: 'Escalated',
    expired: 'Expired',
}

function relTime(iso) {
    if (!iso) return ''
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return ''
    const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000))
    if (days <= 0) return 'today'
    if (days === 1) return '1 day ago'
    if (days < 7) return `${days} days ago`
    if (days < 30) {
        const weeks = Math.round(days / 7)
        return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    }
    return `${Math.round(days / 30)} months ago`
}

export default {
    name: 'ClientApprovalsPanel',
    props: {
        clientId: { type: [Number, String], required: true },
        limit: { type: Number, default: 5 },
        // False renders the rows as plain text: the SA client details page
        // must not link into the /distributor approvals workspace.
        linkRows: { type: Boolean, default: true },
    },
    data() {
        return {
            rows: [],
            loading: true,
            error: '',
        }
    },
    computed: {
        visibleRows() {
            // Rank by urgency, then by updated_at descending. Hard cap
            // to ``limit`` (5 by default) so the panel stays compact.
            const ranked = this.rows.slice().sort((a, b) => {
                const ra = STATUS_RANK[a.status] ?? 99
                const rb = STATUS_RANK[b.status] ?? 99
                if (ra !== rb) return ra - rb
                const ta = a.updated_at ? Date.parse(a.updated_at) : 0
                const tb = b.updated_at ? Date.parse(b.updated_at) : 0
                return tb - ta
            })
            return ranked.slice(0, this.limit)
        },
    },
    watch: {
        clientId() {
            this.load()
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        statusLabel(s) {
            return STATUS_LABEL[s] || s
        },
        staleLabel(s) {
            return STALE_LABEL[s] || s
        },
        relTime,
        async load() {
            this.loading = true
            this.error = ''
            try {
                const res = await api.get(
                    `/approval_records?client_id=${encodeURIComponent(this.clientId)}`,
                )
                const rows = Array.isArray(res?.data?.approval_records)
                    ? res.data.approval_records
                    : []
                // The detail panel cares about non-terminal states.
                // Approved + rejected stay in the workspace surface.
                this.rows = rows.filter(
                    (r) => r.status !== 'approved' && r.status !== 'rejected',
                )
            } catch (e) {
                this.error = 'Could not load approvals.'
                this.rows = []
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.cap__loading,
.cap__error,
.cap__empty {
    padding: 12px;
    color: var(--tf-fg-2);
    font-size: 13px;
}
.cap__error {
    color: #c0392b;
}
.cap__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.cap__link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: var(--tf-surface-alt, #f7f8fa);
    border-radius: var(--tf-radius-xs, 4px);
    text-decoration: none;
    color: var(--tf-fg-1);
    font-size: 13px;
}
.cap__link:hover {
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #d0d4dc);
    padding: 7px 11px;
}
.cap__status {
    flex: 1;
    font-weight: 500;
}
.cap__updated {
    color: var(--tf-fg-3, #6b7280);
}
.cap__stale {
    padding: 2px 8px;
    background: #fff4e0;
    border-radius: 12px;
    color: #b15706;
    font-size: 12px;
}
</style>
